import { ArrowRight, Braces, FileCode2, GitBranch, ShieldAlert } from "lucide-react";
import type { CSSProperties, JSX } from "react";
import { useEffect, useState } from "react";
import type { InventoryDetailTab } from "./InventoryBriefing.js";
import {
  CBOM_CERTIFICATE_IDENTITY_COPY,
  CBOM_KEY_STORAGE_COPY,
  cbomProtocolRoleCopy,
} from "./inventory-copy.js";
import type { InventoryCbomRow } from "./inventory-selection.js";
import { displayRuleId } from "../../app/rule-id.js";
import "./cbom-migration-desk.css";

const RETENTION_HORIZON = "unknown_from_static_source" as const;

function cryptoDisplayName(row: InventoryCbomRow): string {
  const use = row.useContext.replaceAll("_", " ");
  if (row.algorithm?.toLocaleLowerCase() === "ec") {
    return `Elliptic-curve (EC) ${use}`;
  }
  if (
    row.algorithm &&
    /^\d{3,5}$/u.test(row.algorithm) &&
    /key establishment|key generation/u.test(use)
  ) {
    return `RSA-${row.algorithm} ${use}`;
  }
  return row.algorithm ? `${row.algorithm} ${use}` : `Dynamic algorithm ${use}`;
}

function algorithmIdentity(
  row: InventoryCbomRow,
  sourceText: string,
): {
  name: string;
  definition: string;
  quantumEffect: string;
} {
  const algorithm = row.algorithm?.toLocaleLowerCase() ?? "dynamic";
  const source = sourceText.toLocaleLowerCase();
  const isP256 =
    algorithm.includes("prime256v1") ||
    algorithm.includes("p-256") ||
    source.includes("prime256v1") ||
    source.includes("secp256r1") ||
    source.includes("p-256");
  if ((algorithm === "ec" || algorithm.includes("elliptic")) && isP256) {
    return {
      name: "Elliptic-curve key generation — NIST P-256",
      definition:
        "ec means an elliptic-curve public/private key pair. prime256v1 is OpenSSL's name for NIST P-256, also called secp256r1. This call creates a key; it does not prove whether the key signs, performs key agreement, or is stored.",
      quantumEffect:
        "A cryptographically relevant quantum computer using Shor's algorithm could derive the private key from its public key, breaking signatures or key agreement that depend on it.",
    };
  }
  if (algorithm === "ec" || algorithm.includes("elliptic")) {
    return {
      name: "Elliptic-curve (EC) key generation",
      definition:
        "ec means an elliptic-curve public/private key pair. The exact named curve is not present in the bounded source excerpt, so this record does not assume P-256, prime256v1, or another curve. Its consumers determine whether it signs data or establishes a secret.",
      quantumEffect:
        "A sufficiently capable quantum computer using Shor's algorithm could recover the private key from the public key. The exact migration target depends on whether this key signs or establishes secrets.",
    };
  }
  if (algorithm.startsWith("rsa")) {
    return {
      name: `RSA ${row.useContext.replaceAll("_", " ")}`,
      definition:
        "RSA is a classical public-key system used for signatures, encryption, or key transport. The exact security role must be confirmed from its consumers and protocol.",
      quantumEffect:
        "Shor's algorithm would make the RSA private key recoverable from the public key on a sufficiently capable quantum computer.",
    };
  }
  return {
    name: cryptoDisplayName(row),
    definition: `The scanner resolved ${row.api} with algorithm ${row.algorithm ?? "selected at runtime"} and classified its observed purpose as ${row.useContext.replaceAll("_", " ")}.`,
    quantumEffect: classificationHonesty(row),
  };
}

function affectedRepositoryFiles(row: InventoryCbomRow, rows: readonly InventoryCbomRow[]) {
  return [
    ...new Set(
      rows
        .filter(
          (peer) =>
            peer.repositoryId === row.repositoryId &&
            (peer.algorithm === row.algorithm ||
              peer.migrationDependency === row.migrationDependency ||
              (row.findingRuleId && peer.findingRuleId === row.findingRuleId)),
        )
        .map((peer) => peer.location.path),
    ),
  ];
}

function isOrdinaryHashing(row: InventoryCbomRow): boolean {
  return row.useContext === "hashing" || row.useContext === "authentication";
}

function isPqcPriority(row: InventoryCbomRow): boolean {
  return row.algorithmClass === "quantum_vulnerable_public_key" || row.algorithmClass === "hybrid";
}

function hybridReadinessCopy(row: InventoryCbomRow): string {
  if (row.algorithmClass === "hybrid") return "Classical and standardized PQC APIs coexist";
  if (row.algorithmClass === "post_quantum_standardized") return "Standardized PQC API observed";
  if (row.algorithmClass === "quantum_vulnerable_public_key") return "Classical public-key only";
  return "Not a public-key migration item";
}

function classificationHonesty(row: InventoryCbomRow): string {
  if (isOrdinaryHashing(row))
    return "Ordinary hashing or HMAC is not a finding. Keep inventoried, not escalated; do not inflate it into a PQC finding.";
  if (row.algorithmClass === "quantum_vulnerable_public_key")
    return "This quantum-vulnerable public-key observation is a migration review item, not a proven break, live exploit, or PQC-complete claim.";
  if (row.algorithmClass === "hybrid")
    return "Hybrid APIs are a migration-planning signal, not proof that the protocol is PQC-complete.";
  if (row.algorithmClass === "post_quantum_standardized")
    return "Preserve as readiness evidence. A standardized PQC API is not a claim that the whole system is PQC-complete.";
  return "Keep inventoried, not escalated. Static inventory is not a live exploit and does not prove PQC completeness.";
}

function postureCopy(row: InventoryCbomRow): { title: string; lead: string } {
  if (isOrdinaryHashing(row)) {
    return {
      title: "Inventory evidence — no PQC escalation",
      lead: "This call site records hashing or message-authentication use. It contributes to cryptographic inventory and agility planning, but it is not a public-key primitive that Shor’s algorithm would directly break.",
    };
  }
  if (!isPqcPriority(row)) {
    return {
      title: "Cryptographic inventory review",
      lead: "This primitive is security-relevant inventory. Review strength, configuration, key management, and protocol context without presenting the static observation as a quantum-vulnerable public-key finding.",
    };
  }
  return {
    title: "Quantum migration candidate",
    lead: "A classical public-key primitive is present in a security-sensitive code path. A cryptographically relevant quantum computer could invalidate its confidentiality or authenticity guarantee; static evidence does not establish exposure timing.",
  };
}

function sameFilePeers(row: InventoryCbomRow, rows: readonly InventoryCbomRow[]) {
  return rows.filter((peer) => peer.location.path === row.location.path && peer.id !== row.id);
}

function migrationCohort(row: InventoryCbomRow, rows: readonly InventoryCbomRow[]) {
  return rows.filter(
    (peer) =>
      peer.id !== row.id &&
      peer.algorithm !== null &&
      peer.algorithm === row.algorithm &&
      (peer.algorithmClass === "quantum_vulnerable_public_key" ||
        peer.algorithmClass === "hybrid" ||
        row.algorithmClass === "quantum_vulnerable_public_key" ||
        row.algorithmClass === "hybrid"),
  );
}

function migrationStandard(row: InventoryCbomRow) {
  if (!isPqcPriority(row)) {
    return {
      target: "No public-key PQC migration required for this observed use",
      transition: "Preserve algorithm intent behind a versioned provider boundary",
      code: `const digest = cryptoProvider.hash({
  algorithm: "SHA-256",
  purpose: "integrity",
});
const value = await digest.compute(message);`,
    };
  }
  if (/sign|verification/u.test(row.useContext)) {
    return {
      target: "ML-DSA (FIPS 204); evaluate SLH-DSA (FIPS 205) for conservative signatures",
      transition: "Dual-sign or protocol-negotiated hybrid signature",
      code: `const signer = cryptoProvider.signer({\n  classical: "ECDSA-P256",\n  postQuantum: "ML-DSA-65",\n  mode: "dual-sign",\n});\nconst signature = await signer.sign(message);`,
    };
  }
  return {
    target: "ML-KEM (FIPS 203) when this key participates in key establishment",
    transition: "Hybrid classical + ML-KEM-768 negotiation",
    code: `const suite = await cryptoProvider.negotiate({\n  classical: "X25519",\n  postQuantum: "ML-KEM-768",\n  mode: "hybrid",\n});\nconst sharedSecret = await suite.encapsulate(peerPublicKey);`,
  };
}

interface SourceExcerptPayload {
  path: string;
  firstLine: number;
  lines: string[];
  citedLineStart: number;
  citedLineEnd: number;
}

function useSourceExcerpt(row: InventoryCbomRow) {
  const [excerpt, setExcerpt] = useState<SourceExcerptPayload | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const url = new URL(row.sourceHref, window.location.origin);
    url.hash = "";
    url.searchParams.set("format", "json");
    setExcerpt(null);
    setUnavailable(false);
    void fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("source excerpt unavailable");
        return response.json() as Promise<SourceExcerptPayload>;
      })
      .then(setExcerpt)
      .catch(() => {
        if (!controller.signal.aborted) setUnavailable(true);
      });
    return () => controller.abort();
  }, [row.sourceHref]);

  return { excerpt, unavailable };
}

function SourceExcerpt({
  row,
  excerpt,
  unavailable,
}: {
  row: InventoryCbomRow;
  excerpt: SourceExcerptPayload | null;
  unavailable: boolean;
}) {
  return (
    <section className="cbom-migration-desk__source" aria-label="Exact source excerpt">
      <div className="cbom-migration-desk__source-header">
        <div>
          <span>Immutable source excerpt</span>
          <strong>{row.location.path}</strong>
        </div>
        <code>SHA-256 {row.fileSha256.slice(0, 12)}…</code>
      </div>
      {excerpt ? (
        <ol start={excerpt.firstLine}>
          {excerpt.lines.map((line, index) => {
            const lineNumber = excerpt.firstLine + index;
            const cited =
              lineNumber >= excerpt.citedLineStart && lineNumber <= excerpt.citedLineEnd;
            return (
              <li key={lineNumber} className={cited ? "is-cited" : undefined}>
                <code>{line || " "}</code>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="cbom-migration-desk__source-state">
          {unavailable
            ? "The bounded excerpt is unavailable. Open the exact source evidence to inspect it."
            : "Loading the sealed source excerpt…"}
        </p>
      )}
      <p>
        The highlighted line is observed source. Algorithm purpose and migration guidance remain
        explicitly labelled static inference.
      </p>
    </section>
  );
}

function StandardsDecisionPath({ row }: { row: InventoryCbomRow }) {
  const signatureUse = /sign|verification/u.test(row.useContext);
  const publicKeyUse = isPqcPriority(row);
  return (
    <>
      <ol className="cbom-migration-desk__migration-path" aria-label="PQC migration decision path">
        <li style={{ "--step": 0 } as CSSProperties}>
          <span>Observed call site</span>
          <strong>{row.algorithm ?? "dynamic"}</strong>
          <small>{row.api}</small>
        </li>
        <ArrowRight aria-hidden="true" />
        <li style={{ "--step": 1 } as CSSProperties}>
          <span>Security purpose</span>
          <strong>{row.useContext.replaceAll("_", " ")}</strong>
          <small>{cbomProtocolRoleCopy(row)}</small>
        </li>
        <ArrowRight aria-hidden="true" />
        <li style={{ "--step": 2 } as CSSProperties}>
          <span>Quantum exposure</span>
          <strong>{publicKeyUse ? "Shor-vulnerable" : "No direct Shor exposure"}</strong>
          <small>{classificationHonesty(row)}</small>
        </li>
        <ArrowRight aria-hidden="true" />
        <li style={{ "--step": 3 } as CSSProperties}>
          <span>Standards decision</span>
          <strong>{signatureUse ? "ML-DSA / SLH-DSA" : "ML-KEM or signature fork"}</strong>
          <small>Choose by protocol role—not algorithm-name replacement.</small>
        </li>
        <ArrowRight aria-hidden="true" />
        <li style={{ "--step": 4 } as CSSProperties}>
          <span>Validation gate</span>
          <strong>Hybrid + rollback</strong>
          <small>Interoperability, downgrade resistance, vectors, custody.</small>
        </li>
      </ol>
      <section className="cbom-migration-desk__standards" aria-label="NIST post-quantum standards">
        <article className={!signatureUse && publicKeyUse ? "is-recommended" : undefined}>
          <span>FIPS 203</span>
          <strong>ML-KEM</strong>
          <p>Key establishment and shared-secret negotiation. Not a signature replacement.</p>
          <small>
            {!signatureUse && publicKeyUse
              ? "Candidate after protocol confirmation"
              : "Not this observed signature role"}
          </small>
        </article>
        <article className={signatureUse && publicKeyUse ? "is-recommended" : undefined}>
          <span>FIPS 204</span>
          <strong>ML-DSA</strong>
          <p>General-purpose digital signatures where ecosystem support and performance fit.</p>
          <small>
            {signatureUse && publicKeyUse
              ? "Primary signature candidate"
              : "Candidate if this key signs"}
          </small>
        </article>
        <article className={signatureUse && publicKeyUse ? "is-alternative" : undefined}>
          <span>FIPS 205</span>
          <strong>SLH-DSA</strong>
          <p>Stateless hash-based signatures for conservative assurance tradeoffs.</p>
          <small>
            {signatureUse && publicKeyUse
              ? "Evaluate as conservative alternative"
              : "Candidate if conservative signatures are required"}
          </small>
        </article>
      </section>
    </>
  );
}

function Heading({
  icon,
  eyebrow,
  title,
  id,
}: {
  icon: JSX.Element;
  eyebrow: string;
  title: string;
  id: string;
}) {
  return (
    <div className="cbom-migration-desk__heading">
      {icon}
      <div>
        <span>{eyebrow}</span>
        <h3 id={id}>{title}</h3>
      </div>
    </div>
  );
}

function RelationshipTable({ rows }: { rows: readonly InventoryCbomRow[] }) {
  return (
    <div className="cbom-migration-desk__table-wrap">
      <table className="cbom-migration-desk__table">
        <thead>
          <tr>
            <th>Call site</th>
            <th>Algorithm</th>
            <th>Use</th>
            <th>Linkage</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((peer) => (
            <tr key={peer.id}>
              <td>
                <code>
                  {peer.location.path}:{peer.location.lineStart}
                </code>
              </td>
              <td>{peer.algorithm ?? "dynamic"}</td>
              <td>{peer.useContext.replaceAll("_", " ")}</td>
              <td>{peer.findingRuleId ? displayRuleId(peer.findingRuleId) : "Inventory only"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CbomMigrationDesk({
  row,
  cbomRows,
  activeTab,
}: {
  row: InventoryCbomRow;
  cbomRows: readonly InventoryCbomRow[];
  activeTab?: InventoryDetailTab;
}): JSX.Element {
  const sameFile = sameFilePeers(row, cbomRows);
  const cohort = migrationCohort(row, cbomRows);
  const classification = row.algorithmClass.replaceAll("_", " ");
  const standard = migrationStandard(row);
  const posture = postureCopy(row);
  const { excerpt, unavailable } = useSourceExcerpt(row);
  const identity = algorithmIdentity(row, excerpt?.lines.join("\n") ?? "");
  const repositoryFiles = affectedRepositoryFiles(row, cbomRows);

  return (
    <div className="cbom-migration-desk">
      <p className="cbom-migration-desk__kicker">Cryptographic decision record</p>

      {!activeTab || activeTab === "summary" ? (
        <>
          <section
            className="cbom-migration-desk__audience"
            aria-label="Role-specific decision summary"
          >
            <article>
              <span>Cyber finding</span>
              <strong>{identity.name}</strong>
              <p>{identity.quantumEffect}</p>
            </article>
            <article>
              <span>CISO decision</span>
              <strong>
                {repositoryFiles.length} inventoried file{repositoryFiles.length === 1 ? "" : "s"}{" "}
                share this repository risk
              </strong>
              <p>
                Assign the crypto owner, establish the protected data or signature lifetime, and
                fund a protocol-compatible transition. Protected asset: {row.functionalImpact}
              </p>
            </article>
            <article>
              <span>Developer action</span>
              <strong>Trace every consumer of {row.api}</strong>
              <p>
                Start at {row.location.path}:{row.location.lineStart}; confirm whether this key
                signs, establishes secrets, crosses a wire format, or enters a keystore. Migration
                dependency: {row.migrationDependency.replaceAll("_", " ")}.
              </p>
            </article>
          </section>
          <section className="cbom-migration-desk__definition" aria-label="Algorithm definition">
            <span>What the code means</span>
            <p>{identity.definition}</p>
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--classification"
            aria-labelledby="cbom-desk-classification"
          >
            <Heading
              icon={<ShieldAlert size={18} aria-hidden="true" />}
              eyebrow="Executive risk posture"
              title={posture.title}
              id="cbom-desk-classification"
            />
            <p className="cbom-migration-desk__lead">{posture.lead}</p>
            <div className="cbom-migration-desk__table-wrap">
              <table className="cbom-migration-desk__table">
                <tbody>
                  <tr>
                    <th>Classification</th>
                    <td>{classification}</td>
                    <th>Confidence</th>
                    <td>{row.confidenceBand}</td>
                  </tr>
                  <tr>
                    <th>Protocol role</th>
                    <td>{cbomProtocolRoleCopy(row)}</td>
                    <th>Hybrid readiness</th>
                    <td>{hybridReadinessCopy(row)}</td>
                  </tr>
                  <tr>
                    <th>CISO decision</th>
                    <td colSpan={3}>
                      {isPqcPriority(row)
                        ? "Assign an owner, establish data/key lifetime, and fund a compatibility-tested migration."
                        : "Retain as inventory evidence; no PQC escalation without a public-key security role."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="cbom-migration-desk__honesty">{classificationHonesty(row)}</p>
          </section>
        </>
      ) : null}

      {!activeTab || activeTab === "evidence" ? (
        <>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--observed"
            aria-labelledby="cbom-desk-observed"
          >
            <Heading
              icon={<FileCode2 size={18} aria-hidden="true" />}
              eyebrow="Sealed static observation"
              title="Evidence ledger"
              id="cbom-desk-observed"
            />
            <SourceExcerpt row={row} excerpt={excerpt} unavailable={unavailable} />
            <div className="cbom-migration-desk__table-wrap">
              <table className="cbom-migration-desk__table">
                <thead>
                  <tr>
                    <th>Evidence field</th>
                    <th>Recorded value</th>
                    <th>Trust boundary</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Source location</td>
                    <td>
                      <a href={row.sourceHref} target="_blank" rel="noreferrer">
                        {row.location.path}:{row.location.lineStart}
                      </a>
                    </td>
                    <td>Frozen source snapshot</td>
                  </tr>
                  <tr>
                    <td>Resolved API</td>
                    <td>
                      <code>{row.api}</code>
                    </td>
                    <td>{row.algorithmResolution.replaceAll("_", " ")}</td>
                  </tr>
                  <tr>
                    <td>Algorithm / use</td>
                    <td>{cryptoDisplayName(row)}</td>
                    <td>Static call-site inference</td>
                  </tr>
                  <tr>
                    <td>File SHA-256</td>
                    <td>
                      <code>{row.fileSha256}</code>
                    </td>
                    <td>Immutable file binding</td>
                  </tr>
                  <tr>
                    <td>Catalog</td>
                    <td>
                      <code>{row.catalogVersion}</code>
                    </td>
                    <td>Detector taxonomy</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--visual"
            aria-labelledby="cbom-evidence-chain"
          >
            <Heading
              icon={<GitBranch size={18} aria-hidden="true" />}
              eyebrow="Evidence flow"
              title="How this conclusion was formed"
              id="cbom-evidence-chain"
            />
            <ol className="cbom-migration-desk__flow" aria-label="CBOM evidence chain">
              <li>
                <strong>Frozen call site</strong>
                <span>{row.api}</span>
              </li>
              <ArrowRight aria-hidden="true" size={18} />
              <li>
                <strong>Algorithm resolution</strong>
                <span>{row.algorithm ?? "dynamic"}</span>
              </li>
              <ArrowRight aria-hidden="true" size={18} />
              <li>
                <strong>Catalog classification</strong>
                <span>{classification}</span>
              </li>
            </ol>
            <p>
              This chain proves the recorded API and classification at this snapshot. It does not
              prove runtime negotiation, remote peer capability, key custody, or exploitability.
            </p>
          </section>
        </>
      ) : null}

      {!activeTab || activeTab === "investigate" ? (
        <>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--visual"
            aria-labelledby="cbom-blast-radius"
          >
            <Heading
              icon={<GitBranch size={18} aria-hidden="true" />}
              eyebrow="Repository cryptographic exposure"
              title="How this code can affect the wider system"
              id="cbom-blast-radius"
            />
            <p className="cbom-migration-desk__lead">
              This is a migration-impact path, not a claim that an attacker has entered the system.
              Solid stages are recorded facts; file relationships are inventory correlation until a
              call or data-flow edge is stored.
            </p>
            <StandardsDecisionPath row={row} />
            <div className="cbom-migration-desk__impact-strip">
              <div>
                <strong>1</strong>
                <span>selected use</span>
              </div>
              <ArrowRight aria-hidden="true" />
              <div>
                <strong>{sameFile.length}</strong>
                <span>same-file peers</span>
              </div>
              <ArrowRight aria-hidden="true" />
              <div>
                <strong>{cohort.length}</strong>
                <span>same-algorithm cohort</span>
              </div>
            </div>
            <div className="cbom-migration-desk__repository-files">
              <span>Repository-wide file cohort</span>
              <p>
                {repositoryFiles.join(" · ")}. These files share cryptographic migration pressure;
                the current scan does not prove that data flows between them.
              </p>
            </div>
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--same-file"
            aria-labelledby="cbom-desk-same-file"
          >
            <h3 id="cbom-desk-same-file">Other crypto uses in this file</h3>
            {sameFile.length ? (
              <RelationshipTable rows={sameFile} />
            ) : (
              <p>No other cryptographic uses were inventoried in this file.</p>
            )}
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--cohort"
            aria-labelledby="cbom-desk-cohort"
          >
            <h3 id="cbom-desk-cohort">Migration cohort (same algorithm)</h3>
            {cohort.length ? (
              <RelationshipTable rows={cohort} />
            ) : (
              <p>No other inventoried uses share this algorithm.</p>
            )}
          </section>
        </>
      ) : null}

      {!activeTab || activeTab === "review" ? (
        <>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--review"
            aria-labelledby="cbom-review-plan"
          >
            <Heading
              icon={<ShieldAlert size={18} aria-hidden="true" />}
              eyebrow="Decision gates"
              title="Migration plan and accountable evidence"
              id="cbom-review-plan"
            />
            <div className="cbom-migration-desk__table-wrap">
              <table className="cbom-migration-desk__table">
                <thead>
                  <tr>
                    <th>Gate</th>
                    <th>Current evidence</th>
                    <th>Required owner action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Target standard</td>
                    <td>{standard.target}</td>
                    <td>Security architecture selects the protocol-compatible suite.</td>
                  </tr>
                  <tr>
                    <td>Transition mode</td>
                    <td>{standard.transition}</td>
                    <td>
                      Platform engineering validates downgrade resistance and interoperability.
                    </td>
                  </tr>
                  <tr>
                    <td>Retention horizon</td>
                    <td>{RETENTION_HORIZON}</td>
                    <td>
                      Data owner records confidentiality/signature lifetime and regulatory
                      constraints.
                    </td>
                  </tr>
                  <tr>
                    <td>Key custody</td>
                    <td>Unknown from static source</td>
                    <td>
                      Crypto owner identifies keystore, KMS, HSM, rotation, and certificate
                      dependencies.
                    </td>
                  </tr>
                  <tr>
                    <td>Exit criteria</td>
                    <td>Not yet demonstrated</td>
                    <td>
                      Pass vectors, negative tests, fallback tests, performance budgets, and
                      rollback rehearsal.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--code"
            aria-labelledby="cbom-code-guidance"
          >
            <Heading
              icon={<Braces size={18} aria-hidden="true" />}
              eyebrow="Developer guidance"
              title={
                isPqcPriority(row)
                  ? "Illustrative hybrid migration adapter"
                  : "Illustrative crypto-agility adapter"
              }
              id="cbom-code-guidance"
            />
            <p>
              Do not perform a blind algorithm string replacement. Introduce a versioned
              crypto-provider boundary, negotiate capabilities, bind the negotiated suite into the
              transcript, and make downgrade behavior testable.
            </p>
            <pre>
              <code>{standard.code}</code>
            </pre>
            <p className="cbom-migration-desk__honesty">
              <strong>Not a drop-in patch:</strong> the provider API is illustrative. Select a
              validated implementation for the deployment boundary and confirm protocol semantics
              before changing production code.
            </p>
          </section>
          <section
            className="cbom-migration-desk__section cbom-migration-desk__section--unknown"
            aria-labelledby="cbom-desk-unknown"
          >
            <h3 id="cbom-desk-unknown">Evidence still required</h3>
            <div className="cbom-migration-desk__table-wrap">
              <table className="cbom-migration-desk__table">
                <tbody>
                  <tr>
                    <th>Certificate identity</th>
                    <td>{CBOM_CERTIFICATE_IDENTITY_COPY}</td>
                  </tr>
                  <tr>
                    <th>Key storage</th>
                    <td>{CBOM_KEY_STORAGE_COPY}</td>
                  </tr>
                  <tr>
                    <th>Functional impact</th>
                    <td>{row.functionalImpact}</td>
                  </tr>
                  <tr>
                    <th>Migration dependency</th>
                    <td>{row.migrationDependency}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
